import { useMemo, useState, useEffect, useCallback } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useSelectedTeam } from "@/context/teamContext";
import { useOnboarding } from "@/components/general/Onboarding";
import { ONBOARDING_IDS } from "@/components/general/Onboarding/constants";
import {
    linescoresCompareQueryOptions,
    type LinescoresCompareTeam,
    type CompareLinesRequest,
    type MatchAVG,
    type LineOverallStats,
    type PlayerLongResponse,
} from "@/api/lineScores";
import { tournamentTeamsListQueryOptions } from "@/api/tournaments";
import { CalendarDays } from "lucide-react";
import { StatsMobileCards, type StatRow } from "@/components/general/Dashboard/Overview/AnalyticsMobileCards";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/shadcn/dialog";
import { Checkbox } from "@/components/shadcn/checkbox";
import { Button } from "@/components/shadcn/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/shadcn/select";
import { dateFormat, getTeamLogo, getPointsBadge } from "@/lib/utils";
import dayjs from "dayjs";
import ComparisonEngineChart, {
    type RadarPoint,
} from "@/components/general/Dashboard/Comparison/ComparisonEngineChart";

const LINE_COLORS: Record<number, { border: string; bg: string; text: string }> = {
    1: { border: "var(--color-chart-blue)", bg: "rgba(59, 130, 246, 0.1)", text: "var(--color-chart-blue)" },
    2: { border: "#10b981", bg: "rgba(16, 185, 129, 0.1)", text: "#10b981" },
    3: { border: "#f59e0b", bg: "rgba(245, 158, 11, 0.1)", text: "#f59e0b" },
    4: { border: "#ef4444", bg: "rgba(239, 68, 68, 0.1)", text: "#ef4444" },
};

const TEAM_COLORS = {
    chosen: { border: "var(--color-chart-blue)", bg: "rgba(59, 130, 246, 0.1)", text: "var(--color-chart-blue)" },
    opponent: {
        border: "var(--color-general-orange)",
        bg: "rgba(249, 115, 22, 0.1)",
        text: "var(--color-general-orange)",
    },
} as const;

const LINE_STAT_LABELS = [
    { key: "overallDefenseAVG" as const, label: "Def" },
    { key: "overallOffensiveAVG" as const, label: "Off" },
    { key: "overallTeamPlayAVG" as const, label: "TP" },
    { key: "overallEffortAVG" as const, label: "Eff" },
    { key: "overallBonusAVG" as const, label: "Bon" },
];

const LINE_STAT_MAX: Record<string, number> = {
    overallDefenseAVG: 20,
    overallOffensiveAVG: 25,
    overallTeamPlayAVG: 29,
    overallEffortAVG: 22,
    overallBonusAVG: 7,
};

const toStatRow = (p: PlayerLongResponse): StatRow => ({
    playerId: p.playerId,
    displayName: `${p.firstName} ${p.lastName}`,
    role: p.role,
    subScores: [
        { label: "Def", value: p.pointsDefensive },
        { label: "Off", value: p.pointsOffensive },
        { label: "TP", value: p.pointsTeamPlay },
        { label: "Eff", value: p.pointsEffort },
        { label: "Bon", value: p.pointsBonus },
    ],
    totalPoints: p.totalPoints,
});

const StatsBar = ({
    avg,
    side,
    lineColors,
}: {
    avg: LineOverallStats;
    side: "chosen" | "opponent";
    lineColors: { text: string; border: string; bg: string };
}) => {
    const teamColors = TEAM_COLORS[side];
    return (
        <div
            className="rounded-lg border p-3 w-full"
            style={{ borderColor: teamColors.border, backgroundColor: teamColors.bg }}
        >
            <div className="grid grid-cols-6 gap-2">
                {[
                    { label: "Def", value: avg.overallDefenseAVG },
                    { label: "Off", value: avg.overallOffensiveAVG },
                    { label: "TP", value: avg.overallTeamPlayAVG },
                    { label: "Eff", value: avg.overallEffortAVG },
                    { label: "Bon", value: avg.overallBonusAVG },
                ].map(({ label, value }) => (
                    <div key={label} className="text-center">
                        <div className="text-[10px] text-on-surface-variant mb-0.5">{label}</div>
                        <div className="text-sm font-bold text-on-surface">{value || "—"}</div>
                    </div>
                ))}
                <div className="text-center">
                    <div className="text-[10px] font-semibold mb-0.5" style={{ color: lineColors.text }}>
                        Total
                    </div>
                    <div className="text-sm font-extrabold" style={{ color: lineColors.text }}>
                        {avg.overallTotalAVG || "—"}
                    </div>
                </div>
            </div>
        </div>
    );
};

const TeamHeader = ({ teamName, side }: { teamName: string; side: "chosen" | "opponent" }) => {
    const logo = getTeamLogo(teamName);
    const color = TEAM_COLORS[side].text;
    return (
        <div className="flex items-center gap-2 px-1">
            {logo && <img src={logo} alt={teamName} className="h-4 w-auto object-contain shrink-0" />}
            <span className="text-xs font-semibold truncate" style={{ color }} title={teamName}>
                {teamName}
            </span>
        </div>
    );
};

function LocalPlayerTable({
    rows,
    teamId,
    padKey,
}: {
    rows: (PlayerLongResponse | null)[];
    teamId: number;
    padKey: string;
}) {
    let rank = 0;
    let padIdx = 0;
    return (
        <div className="rounded-md overflow-hidden border border-outline-variant/20 w-full">
            <table className="w-full border-collapse text-xs">
                <thead>
                    <tr className="bg-surface-container-high border-b border-outline-variant/20">
                        <th className="py-1.5 pl-2 pr-1 text-left text-on-surface-variant font-semibold w-7">#</th>
                        <th className="py-1.5 px-1 text-left text-on-surface-variant font-semibold">Player</th>
                        <th className="py-1.5 px-1 text-center text-on-surface-variant font-semibold w-10">Pos</th>
                        <th className="py-1.5 px-1 text-center text-on-surface-variant font-semibold w-7">Def</th>
                        <th className="py-1.5 px-1 text-center text-on-surface-variant font-semibold w-7">Off</th>
                        <th className="py-1.5 px-1 text-center text-on-surface-variant font-semibold w-6">TP</th>
                        <th className="py-1.5 px-1 text-center text-on-surface-variant font-semibold w-7">Eff</th>
                        <th className="py-1.5 px-1 text-center text-on-surface-variant font-semibold w-7">Bon</th>
                        <th className="py-1.5 pl-1 pr-2 text-center text-on-surface-variant font-semibold w-9">Tot</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => {
                        const rowBg = i % 2 === 0 ? "bg-surface-container-lowest" : "bg-surface-container-low";
                        if (!row) {
                            padIdx++;
                            return (
                                <tr key={`${padKey}-empty-${padIdx}`} className={`${rowBg} h-9`}>
                                    <td colSpan={9} />
                                </tr>
                            );
                        }
                        rank++;
                        const { bg, text, border } = getPointsBadge(row.totalPoints);
                        return (
                            <tr key={row.playerId} className={`${rowBg} border-b border-outline-variant/10 h-9`}>
                                <td className="py-1.5 pl-2 pr-1 font-headline font-bold text-on-surface-variant whitespace-nowrap">
                                    #{String(rank).padStart(2, "0")}
                                </td>
                                <td className="py-1.5 px-1 min-w-0 max-w-0">
                                    <Link
                                        to="/player/$playerId"
                                        params={{ playerId: String(row.playerId) }}
                                        search={{ teamId }}
                                        className="font-bold text-on-surface hover:text-primary hover:underline transition-colors truncate block"
                                    >
                                        {row.firstName} {row.lastName}
                                    </Link>
                                </td>
                                <td className="py-1.5 px-1 text-center">
                                    <span className="px-1 py-0.5 bg-surface-container-highest rounded text-[9px] font-bold uppercase text-on-surface-variant">
                                        {row.role}
                                    </span>
                                </td>
                                <td className="py-1.5 px-1 text-center text-on-surface tabular-nums">
                                    {row.pointsDefensive}
                                </td>
                                <td className="py-1.5 px-1 text-center text-on-surface tabular-nums">
                                    {row.pointsOffensive}
                                </td>
                                <td className="py-1.5 px-1 text-center text-on-surface tabular-nums">
                                    {row.pointsTeamPlay}
                                </td>
                                <td className="py-1.5 px-1 text-center text-on-surface tabular-nums">
                                    {row.pointsEffort}
                                </td>
                                <td className="py-1.5 px-1 text-center text-on-surface tabular-nums">
                                    {row.pointsBonus}
                                </td>
                                <td className="py-1.5 pl-1 pr-2 text-center">
                                    <span
                                        className={`inline-block w-full text-center tabular-nums py-0.5 rounded font-bold border ${bg} ${text} ${border}`}
                                    >
                                        {row.totalPoints}
                                    </span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

const TeamColumn = ({
    avg,
    teamName,
    teamId,
    rows,
    side,
    lineColors,
    padKey,
    inactive = false,
}: {
    avg: LineOverallStats;
    teamName: string;
    teamId: number;
    rows: (PlayerLongResponse | null)[];
    side: "chosen" | "opponent";
    lineColors: { border: string; bg: string; text: string };
    padKey: string;
    inactive?: boolean;
}) => (
    <div
        className={`flex flex-col gap-2 transition-opacity ${inactive ? "opacity-30 pointer-events-none select-none" : ""}`}
    >
        <TeamHeader teamName={teamName} side={side} />
        <div className="md:hidden">
            <StatsMobileCards data={rows.filter((r): r is PlayerLongResponse => r !== null).map(toStatRow)} />
        </div>
        <div className="hidden md:block">
            <LocalPlayerTable rows={rows} teamId={teamId} padKey={padKey} />
        </div>
        <StatsBar avg={avg} side={side} lineColors={lineColors} />
    </div>
);

interface LineSectionProps {
    lineNumber: 1 | 2 | 3 | 4;
    team1: LinescoresCompareTeam;
    team2: LinescoresCompareTeam | null;
}

const EMPTY_AVG: LineOverallStats = {
    overallDefenseAVG: 0,
    overallOffensiveAVG: 0,
    overallTeamPlayAVG: 0,
    overallEffortAVG: 0,
    overallBonusAVG: 0,
    overallTotalAVG: 0,
};

function LineSection({ lineNumber, team1, team2 }: LineSectionProps) {
    const colors = LINE_COLORS[lineNumber] ?? LINE_COLORS[1];
    const lineKey = `line${lineNumber}` as "line1" | "line2" | "line3" | "line4";
    const t1avg = team1.average[lineKey];
    const t2avg = team2?.average[lineKey] ?? EMPTY_AVG;

    const t1sorted = useMemo(() => {
        const unique = [
            ...new Map(
                team1.playersByAverage.filter((p) => p.line === lineNumber).map((p) => [p.playerId, p] as const)
            ).values(),
        ];
        return unique.sort((a, b) => (b.totalPoints ?? 0) - (a.totalPoints ?? 0));
    }, [team1, lineNumber]);
    const t2sorted = useMemo(() => {
        if (!team2) return [];
        const unique = [
            ...new Map(
                team2.playersByAverage.filter((p) => p.line === lineNumber).map((p) => [p.playerId, p] as const)
            ).values(),
        ];
        return unique.sort((a, b) => (b.totalPoints ?? 0) - (a.totalPoints ?? 0));
    }, [team2, lineNumber]);

    const maxCount = Math.max(t1sorted.length, team2 ? t2sorted.length : t1sorted.length);

    const pad = (arr: PlayerLongResponse[], len: number): (PlayerLongResponse | null)[] => {
        const sliced = arr.slice(0, len);
        return [...sliced, ...Array<null>(Math.max(0, len - sliced.length)).fill(null)];
    };

    const t1rows = pad(t1sorted, maxCount);
    const t2rows = team2 ? pad(t2sorted, maxCount) : Array<null>(maxCount).fill(null);
    const t2TeamName = team2?.teamName ?? "Opponent";
    const t2TeamId = team2?.teamId ?? 0;

    const radarData = useMemo<RadarPoint[]>(
        () =>
            LINE_STAT_LABELS.map(({ key, label }) => ({
                stat: label,
                A: Math.round((t1avg[key] / (LINE_STAT_MAX[key] || 1)) * 100),
                B: Math.round((t2avg[key] / (LINE_STAT_MAX[key] || 1)) * 100),
                fullMark: 100,
                aRaw: t1avg[key],
                bRaw: t2avg[key],
            })),
        [t1avg, t2avg]
    );

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 px-1">
                <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: colors.text }} />
                <span className="text-sm font-semibold uppercase tracking-wide" style={{ color: colors.text }}>
                    Line {lineNumber}
                </span>
                <div className="flex-1 h-px bg-outline-variant/40" />
            </div>

            <div className="xl:hidden flex flex-col gap-4">
                <TeamColumn
                    avg={t1avg}
                    teamName={team1.teamName}
                    teamId={team1.teamId}
                    rows={t1rows}
                    side="chosen"
                    lineColors={colors}
                    padKey={`l${lineNumber}-t1`}
                />
                <TeamColumn
                    avg={t2avg}
                    teamName={t2TeamName}
                    teamId={t2TeamId}
                    rows={t2rows}
                    side="opponent"
                    lineColors={colors}
                    padKey={`l${lineNumber}-t2`}
                    inactive={!team2}
                />
                <div className={`h-[260px] transition-opacity ${!team2 ? "opacity-40" : ""}`}>
                    <ComparisonEngineChart data={radarData} playerAName={team1.teamName} playerBName={t2TeamName} />
                </div>
            </div>

            <div className="hidden xl:grid grid-cols-[1fr_280px_1fr] gap-4 items-start">
                <TeamColumn
                    avg={t1avg}
                    teamName={team1.teamName}
                    teamId={team1.teamId}
                    rows={t1rows}
                    side="chosen"
                    lineColors={colors}
                    padKey={`l${lineNumber}-t1`}
                />
                <div className={`h-[260px] self-center transition-opacity ${!team2 ? "opacity-40" : ""}`}>
                    <ComparisonEngineChart data={radarData} playerAName={team1.teamName} playerBName={t2TeamName} />
                </div>
                <TeamColumn
                    avg={t2avg}
                    teamName={t2TeamName}
                    teamId={t2TeamId}
                    rows={t2rows}
                    side="opponent"
                    lineColors={colors}
                    padKey={`l${lineNumber}-t2`}
                    inactive={!team2}
                />
            </div>
        </div>
    );
}

interface MatchSelectModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (ids: number[]) => void;
    teamName: string;
    matches: MatchAVG[];
    currentSelectedIds: number[];
}

function MatchSelectModal({ open, onClose, onConfirm, teamName, matches, currentSelectedIds }: MatchSelectModalProps) {
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    useEffect(() => {
        if (open) setSelectedIds([...currentSelectedIds]);
    }, [open, currentSelectedIds]);

    const sortedMatches = useMemo(() => {
        return [...matches].sort((a, b) => new Date(b.matchDate).getTime() - new Date(a.matchDate).getTime());
    }, [matches]);

    const toggleMatch = (id: number) =>
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="p-0 w-[calc(100vw-2rem)] max-w-[460px] bg-surface-container-low border border-outline-variant/20 rounded-xl shadow-2xl">
                <DialogHeader className="px-6 py-4 border-b border-outline-variant/20 bg-surface-container">
                    <DialogTitle className="text-on-surface font-semibold flex items-center gap-2">
                        <CalendarDays className="w-4 h-4 text-blue-400" />
                        Select Matches — {teamName}
                    </DialogTitle>
                </DialogHeader>
                <div className="px-6 py-4 flex flex-col gap-2 max-h-[420px] overflow-y-auto">
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-on-surface-variant">
                            {selectedIds.length === 0
                                ? "All matches (default)"
                                : `${selectedIds.length} of ${matches.length} selected`}
                        </span>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setSelectedIds(matches.map((m) => m.matchId))}
                                className="text-xs text-blue-400 hover:text-blue-300 cursor-pointer transition-colors"
                            >
                                Select all
                            </button>
                            <span className="text-on-surface-variant">·</span>
                            <button
                                onClick={() => setSelectedIds([])}
                                className="text-xs text-on-surface-variant hover:text-red-400 cursor-pointer transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                    {sortedMatches.map((match) => {
                        const isSelected = selectedIds.includes(match.matchId);
                        const resultColor =
                            match.result === "W"
                                ? "text-green-400"
                                : match.result === "L"
                                  ? "text-red-400"
                                  : "text-yellow-400";
                        return (
                            <label
                                key={match.matchId}
                                className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                    isSelected
                                        ? "border-blue-500/60 bg-blue-900/20"
                                        : "border-outline-variant/20 bg-surface-container hover:border-outline-variant/40"
                                }`}
                            >
                                <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={() => toggleMatch(match.matchId)}
                                    className="border-outline-variant/40 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 shrink-0"
                                />
                                <div className="flex-1 min-w-0 text-sm text-on-surface">vs {match.enemyTeam}</div>
                                <div className="text-sm text-on-surface-variant text-center shrink-0">
                                    {dayjs(match.matchDate).format(dateFormat)}
                                </div>
                                <span className={`text-xs font-bold shrink-0 ${resultColor}`}>
                                    {match.result} {match.chosenTeamScore}–{match.opponentScore}
                                </span>
                            </label>
                        );
                    })}
                </div>
                <div className="flex justify-end gap-2 px-6 py-4 bg-surface-container border-t border-outline-variant/20">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm text-on-surface hover:text-on-surface border border-outline-variant/20 hover:border-outline-variant/40 rounded-md transition-colors cursor-pointer"
                    >
                        Cancel
                    </button>
                    <Button
                        variant="default"
                        onClick={() => onConfirm(selectedIds)}
                        className="bg-blue-600 text-white hover:bg-blue-700 text-sm"
                    >
                        Apply
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

interface TournamentLinesCompareProps {
    tournamentId: number;
}

export function TournamentLinesCompare({ tournamentId }: TournamentLinesCompareProps) {
    const { teamId: coreTeamId } = useSelectedTeam();
    const { registerBeforeTournamentLinesTour } = useOnboarding();
    const [hasExplicitOpponent, setHasExplicitOpponent] = useState(false);
    const [request, setRequest] = useState<CompareLinesRequest>({
        coreTeamId: coreTeamId ?? 0,
        opponentTeamId: 0,
        chosenMatchesIds: [],
        team2MatchesIds: [],
    });
    const [allAvailableMatches, setAllAvailableMatches] = useState<{ team1: MatchAVG[]; team2: MatchAVG[] } | null>(
        null
    );
    const [matchModal, setMatchModal] = useState<{ open: boolean; target: "team1" | "team2" | null }>({
        open: false,
        target: null,
    });
    const [ownTeamData, setOwnTeamData] = useState<LinescoresCompareTeam | null>(null);

    const { data: teamsData } = useQuery(tournamentTeamsListQueryOptions(tournamentId));
    const opponentTeams = useMemo(
        () => teamsData?.filter((t) => t.teamId !== coreTeamId) ?? [],
        [teamsData, coreTeamId]
    );

    const { data, isLoading } = useQuery({
        ...linescoresCompareQueryOptions(tournamentId, request),
        enabled: request.opponentTeamId > 0,
    });

    useEffect(() => {
        setAllAvailableMatches(null);
        setOwnTeamData(null);
        setHasExplicitOpponent(false);
        setRequest({ coreTeamId: coreTeamId ?? 0, opponentTeamId: 0, chosenMatchesIds: [], team2MatchesIds: [] });
    }, [tournamentId, coreTeamId]);

    useEffect(() => {
        if (opponentTeams.length > 0 && !hasExplicitOpponent) {
            setRequest((prev) => {
                if (prev.opponentTeamId === 0) {
                    return { ...prev, opponentTeamId: opponentTeams[0].teamId };
                }
                return prev;
            });
        }
    }, [opponentTeams, hasExplicitOpponent]);

    useEffect(() => {
        if (data) {
            setOwnTeamData(data.chosenTeamData);
            if (!allAvailableMatches) {
                setAllAvailableMatches({
                    team1: data.chosenTeamData.matches ?? [],
                    team2: data.team2.matches ?? [],
                });
            }
        }
    }, [data, allAvailableMatches]);

    const openModal = (target: "team1" | "team2", e: React.MouseEvent) => {
        e.stopPropagation();
        setMatchModal({ open: true, target });
    };

    const handleModalConfirm = (ids: number[]) => {
        if (matchModal.target === "team1") {
            setRequest((prev) => ({ ...prev, chosenMatchesIds: ids }));
        } else if (matchModal.target === "team2") {
            setRequest((prev) => ({ ...prev, team2MatchesIds: ids }));
        }
        setMatchModal({ open: false, target: null });
    };

    const handleOpponentChange = useCallback(
        (value: string) => {
            setAllAvailableMatches(null);
            setHasExplicitOpponent(true);
            setRequest({
                coreTeamId: coreTeamId ?? 0,
                opponentTeamId: parseInt(value),
                chosenMatchesIds: [],
                team2MatchesIds: [],
            });
        },
        [coreTeamId]
    );

    useEffect(() => {
        if (opponentTeams.length === 0) return;
        const firstTeamId = opponentTeams[0].teamId;
        return registerBeforeTournamentLinesTour(() => {
            if (!hasExplicitOpponent) {
                handleOpponentChange(String(firstTeamId));
            }
        });
    }, [registerBeforeTournamentLinesTour, opponentTeams, hasExplicitOpponent, handleOpponentChange]);

    const team1 = data?.chosenTeamData ?? ownTeamData;
    const team2 = hasExplicitOpponent ? (data?.team2 ?? null) : null;

    const team1MatchCount = allAvailableMatches?.team1.length ?? data?.chosenTeamData.matches.length ?? 0;
    const team2MatchCount = allAvailableMatches?.team2.length ?? data?.team2.matches.length ?? 0;
    const team1FilteredCount = request.chosenMatchesIds.length;
    const team2FilteredCount = request.team2MatchesIds.length;

    return (
        <>
            <MatchSelectModal
                open={matchModal.open}
                onClose={() => setMatchModal({ open: false, target: null })}
                onConfirm={handleModalConfirm}
                teamName={matchModal.target === "team1" ? (team1?.teamName ?? "") : (team2?.teamName ?? "")}
                matches={
                    matchModal.target === "team1"
                        ? (allAvailableMatches?.team1 ?? data?.chosenTeamData.matches ?? [])
                        : (allAvailableMatches?.team2 ?? data?.team2.matches ?? [])
                }
                currentSelectedIds={matchModal.target === "team1" ? request.chosenMatchesIds : request.team2MatchesIds}
            />

            <div className="flex-1 min-h-0 flex flex-col gap-6 overflow-y-auto pr-1 scrollbar-thin scrollbar-track-surface-container scrollbar-thumb-blue-500">
                <div id="onboarding-team-h2h-match-selector" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button
                        className="glass card rounded-xl px-5 py-3 flex items-center gap-3 min-h-16 text-left cursor-pointer hover:border-blue-500/50 hover:bg-blue-900/10 transition-colors group disabled:opacity-40 disabled:pointer-events-none"
                        onClick={(e) => openModal("team1", e)}
                        disabled={!team1}
                    >
                        <div
                            className="w-2 h-8 rounded-full shrink-0"
                            style={{ backgroundColor: "var(--color-chart-blue)" }}
                        />
                        <div className="flex-1 min-w-0">
                            <div className="text-base font-semibold text-on-surface truncate">
                                {team1?.teamName ?? "…"}
                            </div>
                            {team1 && (
                                <div className="text-xs text-on-surface-variant">
                                    {team1FilteredCount > 0 && team1FilteredCount < team1MatchCount ? (
                                        <span className="text-blue-400">
                                            {team1FilteredCount} / {team1MatchCount} matches
                                        </span>
                                    ) : (
                                        <span>
                                            {team1MatchCount} match{team1MatchCount !== 1 ? "es" : ""}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        <CalendarDays className="w-4 h-4 text-on-surface-variant group-hover:text-blue-400 transition-colors shrink-0" />
                    </button>

                    <div
                        id={ONBOARDING_IDS.TOURNAMENT_LINES_OPPONENT_SELECT}
                        className={`glass card rounded-xl px-5 py-3 flex items-center gap-3 min-h-16 transition-colors ${
                            hasExplicitOpponent && team2
                                ? "cursor-pointer hover:border-orange-500/50 hover:bg-orange-900/10 group"
                                : ""
                        }`}
                        onClick={(e) => {
                            if (hasExplicitOpponent && team2) openModal("team2", e);
                        }}
                    >
                        <div
                            className="w-2 h-8 rounded-full shrink-0"
                            style={{ backgroundColor: "var(--color-general-orange)" }}
                        />
                        <div className="flex-1 min-w-0">
                            <div onClick={(e) => e.stopPropagation()}>
                                <Select
                                    value={
                                        hasExplicitOpponent && request.opponentTeamId
                                            ? String(request.opponentTeamId)
                                            : ""
                                    }
                                    onValueChange={handleOpponentChange}
                                >
                                    <SelectTrigger className="h-auto w-full border border-outline-variant/40 bg-surface-container-highest rounded-md px-3 py-1.5 shadow-none focus:ring-0 focus-visible:ring-0 [&>svg]:ml-2 [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:opacity-60">
                                        <SelectValue
                                            placeholder={
                                                <span className="text-sm font-semibold text-on-surface-variant">
                                                    Select opponent…
                                                </span>
                                            }
                                        >
                                            {hasExplicitOpponent && team2 ? (
                                                <span className="text-sm font-semibold text-on-surface">
                                                    {team2.teamName}
                                                </span>
                                            ) : undefined}
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {opponentTeams.map((team) => (
                                            <SelectItem key={team.teamId} value={String(team.teamId)}>
                                                {team.teamName}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {hasExplicitOpponent && team2 && (
                                <div className="text-xs text-on-surface-variant mt-0.5">
                                    {team2FilteredCount > 0 && team2FilteredCount < team2MatchCount ? (
                                        <span style={{ color: "var(--color-general-orange)" }}>
                                            {team2FilteredCount} / {team2MatchCount} matches
                                        </span>
                                    ) : (
                                        <span>
                                            {team2MatchCount} match{team2MatchCount !== 1 ? "es" : ""}
                                        </span>
                                    )}
                                </div>
                            )}
                            {isLoading && hasExplicitOpponent && !team2 && (
                                <div className="text-xs text-on-surface-variant mt-0.5">Loading…</div>
                            )}
                        </div>
                        {hasExplicitOpponent && team2 && (
                            <CalendarDays className="w-4 h-4 text-on-surface-variant group-hover:text-orange-400 transition-colors shrink-0" />
                        )}
                    </div>
                </div>

                {team1 ? (
                    <>
                        {!hasExplicitOpponent && (
                            <div className="sm:hidden flex flex-col items-center justify-center gap-2 py-8 text-on-surface-variant text-sm">
                                <span>Select an opponent above to see the comparison</span>
                            </div>
                        )}
                        <div
                            id="onboarding-team-h2h-lines"
                            className={`flex flex-col gap-6 ${!hasExplicitOpponent ? "hidden sm:flex" : ""}`}
                        >
                            {([1, 2, 3, 4] as const).map((lineNumber) => (
                                <LineSection key={lineNumber} lineNumber={lineNumber} team1={team1} team2={team2} />
                            ))}
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex items-center justify-center">
                        <p className="text-on-surface-variant text-sm">Loading…</p>
                    </div>
                )}
            </div>
        </>
    );
}
